import React, { useEffect, useState } from "react";
import { Navigate, NavLink, Route, Routes } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Home as HomeIcon, FileText, Calculator, FolderClock, Database, LogOut, User, FileLock2 } from "lucide-react";
import IntakeForm from "./IntakeForm";
import EstimatesHistory from "./EstimatesHistory";
import AbaNinjaPanel from "./AbaNinjaPanel";
import PortalDocuments from "./PortalDocuments";

function PortalHome({ user }) {
  return (
    <div data-testid="portal-home">
      <h1 className="font-heading text-4xl font-bold text-[#1A2433] mb-2">Willkommen, {user.first_name}!</h1>
      <p className="text-slate-600 mb-8">Ihr digitaler Treuhand-Schreibtisch.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <PortalCard to="/portal/onboarding" icon={FileText} title="Onboarding-Formular" text="Erfassen Sie Ihre Firmendaten für die Gründung oder Übernahme." />
        <PortalCard to="/portal/dokumente" icon={FileLock2} title="Meine Dokumente" text="Ausweis, Pass und Firmendokumente sicher hochladen." />
        <PortalCard to="/rechner" icon={Calculator} title="Kostenrechner" text="Kalkulieren Sie und speichern Sie Ihre individuelle Offerte." external />
        <PortalCard to="/portal/angebote" icon={FolderClock} title="Meine Angebote" text="Gespeicherte Kalkulationen und Anfragen einsehen." />
        <PortalCard to="/portal/abaninja" icon={Database} title="AbaNinja" text="Status der Buchhaltungs-Schnittstelle und synchronisierte Belege." />
        <PortalCard to="/vorratsgesellschaften" icon={Database} title="Vorratsgesellschaften" text="Aktuelle Firmen zum sofortigen Kauf." external />
      </div>
    </div>
  );
}

function PortalCard({ to, icon: Icon, title, text, external }) {
  const Tag = external ? "a" : NavLink;
  const props = external ? { href: to } : { to };
  return (
    <Tag {...props} className="mbw-card p-6 block" data-testid={`portal-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="w-11 h-11 rounded-lg bg-[#1A2433] flex items-center justify-center mb-4">
        <Icon className="w-5 h-5 text-[#DC2626]" />
      </div>
      <h3 className="font-heading text-lg font-semibold text-[#1A2433] mb-1">{title}</h3>
      <p className="text-sm text-slate-600">{text}</p>
    </Tag>
  );
}

export default function PortalLayout() {
  const { user, logout } = useAuth();

  if (user === null) return <div className="pt-32 text-center">Lade…</div>;
  if (!user || !user.id) return <Navigate to="/login" replace />;

  return (
    <div className="pt-20 min-h-screen bg-slate-50" data-testid="portal-layout">
      <div className="mbw-container py-10 grid lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3" data-testid="portal-sidebar">
          <div className="bg-white rounded-2xl border border-slate-200 p-5 sticky top-28">
            <div className="flex items-center gap-3 pb-5 border-b border-slate-200">
              <div className="w-10 h-10 rounded-full bg-[#1A2433] flex items-center justify-center text-[#DC2626] font-heading font-bold">
                {user.first_name?.[0]}{user.last_name?.[0]}
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-[#1A2433] truncate">{user.first_name} {user.last_name}</div>
                <div className="text-xs text-slate-500 truncate">{user.email}</div>
              </div>
            </div>
            <nav className="mt-4 flex flex-col gap-1">
              <SideLink to="/portal" end icon={HomeIcon} label="Übersicht" />
              <SideLink to="/portal/onboarding" icon={FileText} label="Onboarding-Formular" />
              <SideLink to="/portal/dokumente" icon={FileLock2} label="Meine Dokumente" />
              <SideLink to="/portal/angebote" icon={FolderClock} label="Meine Angebote" />
              <SideLink to="/portal/abaninja" icon={Database} label="AbaNinja" />
              <button
                onClick={logout}
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg mt-4"
                data-testid="portal-logout-btn"
              >
                <LogOut className="w-4 h-4" /> Abmelden
              </button>
            </nav>
          </div>
        </aside>

        <main className="lg:col-span-9">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 md:p-10">
            <Routes>
              <Route index element={<PortalHome user={user} />} />
              <Route path="onboarding" element={<IntakeForm />} />
              <Route path="dokumente" element={<PortalDocuments />} />
              <Route path="angebote" element={<EstimatesHistory />} />
              <Route path="abaninja" element={<AbaNinjaPanel />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
}

function SideLink({ to, end, icon: Icon, label }) {
  return (
    <NavLink
      to={to} end={end}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
          isActive ? "bg-[#1A2433] text-white" : "text-slate-700 hover:bg-slate-100"
        }`
      }
      data-testid={`sidelink-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <Icon className="w-4 h-4" /> {label}
    </NavLink>
  );
}
